import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Notification, NotificationPreferences, DocumentVerificationNotification, NotificationAnalytics, NotificationData } from '@/types/notification'
import { notificationApi } from '@/services/api'
import { showSnackbar } from './uiSlice'

interface NotificationsState {
    notifications: Notification[]
    unreadCount: number
    isLoading: boolean
    error: string | null
    preferences: NotificationPreferences | null
    hasMore: boolean
    currentPage: number
    totalPages: number
    // Document verification specific state
    verificationNotifications: Notification[]
    verificationStats: {
        totalVerificationNotifications: number
        unreadVerificationNotifications: number
        notificationsByStatus: Record<string, number>
        notificationsByDocumentType: Record<string, number>
        readRate: number
    } | null
    documentNotifications: Record<string, Notification[]> // documentId -> notifications[]
    // Phase 4: Advanced features state
    analytics: NotificationAnalytics | null
    preferencesLoading: boolean
    analyticsLoading: boolean
}

const initialState: NotificationsState = {
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    error: null,
    preferences: null,
    hasMore: true,
    currentPage: 1,
    totalPages: 1,
    // Document verification specific initial state
    verificationNotifications: [],
    verificationStats: null,
    documentNotifications: {},
    // Phase 4: Advanced features initial state
    analytics: null,
    preferencesLoading: false,
    analyticsLoading: false,
}

// Async thunks
export const fetchNotifications = createAsyncThunk(
    'notifications/fetchNotifications',
    async (params: { page?: number; limit?: number; unreadOnly?: boolean }, { rejectWithValue }) => {
        try {
            const response = await notificationApi.getNotifications(params)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch notifications'
            return rejectWithValue(message)
        }
    }
)

export const markAsRead = createAsyncThunk(
    'notifications/markAsRead',
    async (notificationId: string, { rejectWithValue }) => {
        try {
            const response = await notificationApi.markAsRead(notificationId)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data.notification
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to mark as read'
            return rejectWithValue(message)
        }
    }
)

export const markAllAsRead = createAsyncThunk(
    'notifications/markAllAsRead',
    async (_, { rejectWithValue }) => {
        try {
            const response = await notificationApi.markAllAsRead()
            return response.data.count
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to mark all as read'
            return rejectWithValue(message)
        }
    }
)

export const deleteNotification = createAsyncThunk(
    'notifications/deleteNotification',
    async (notificationId: string, { rejectWithValue, dispatch }) => {
        try {
            await notificationApi.deleteNotification(notificationId)
            dispatch(showSnackbar({ message: 'Notification deleted', type: 'success' }))
            return notificationId
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to delete notification'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const deleteAllNotifications = createAsyncThunk(
    'notifications/deleteAll',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const response = await notificationApi.deleteAllNotifications()
            dispatch(showSnackbar({ message: 'All notifications deleted', type: 'success' }))
            return response.data.count
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to delete all notifications'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const fetchUnreadCount = createAsyncThunk(
    'notifications/fetchUnreadCount',
    async (_, { rejectWithValue }) => {
        try {
            const response = await notificationApi.getUnreadCount()
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data.count
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch unread count'
            return rejectWithValue(message)
        }
    }
)

export const fetchNotificationPreferences = createAsyncThunk(
    'notifications/fetchPreferences',
    async (_, { rejectWithValue }) => {
        try {
            const response = await notificationApi.getPreferences()
            // Handle nested response: { success: true, data: { preferences: {...} } }
            return response.data.data.preferences
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch preferences'
            return rejectWithValue(message)
        }
    }
)

export const updateNotificationPreferences = createAsyncThunk(
    'notifications/updatePreferences',
    async (preferences: Partial<NotificationPreferences>, { rejectWithValue, dispatch }) => {
        try {
            const response = await notificationApi.updatePreferences(preferences)
            dispatch(showSnackbar({ message: 'Notification preferences updated', type: 'success' }))
            // Handle nested response: { success: true, data: { preferences: {...} } }
            return response.data.data.preferences
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to update preferences'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

// Document verification specific async thunks
export const fetchVerificationNotifications = createAsyncThunk(
    'notifications/fetchVerificationNotifications',
    async (params: {
        page?: number
        limit?: number
        documentType?: NotificationData['documentType']
        verificationStatus?: NotificationData['verificationStatus']
        unreadOnly?: boolean
    }, { rejectWithValue }) => {
        try {
            const response = await notificationApi.getVerificationNotifications(params)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch verification notifications'
            return rejectWithValue(message)
        }
    }
)

export const fetchDocumentNotifications = createAsyncThunk(
    'notifications/fetchDocumentNotifications',
    async (documentId: string, { rejectWithValue }) => {
        try {
            const response = await notificationApi.getDocumentNotifications(documentId)
            return { documentId, data: response.data }
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch document notifications'
            return rejectWithValue(message)
        }
    }
)

export const fetchVerificationStats = createAsyncThunk(
    'notifications/fetchVerificationStats',
    async (params: { start?: string; end?: string }, { rejectWithValue }) => {
        try {
            const response = await notificationApi.getVerificationStats(params)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch verification stats'
            return rejectWithValue(message)
        }
    }
)

// Phase 4: Advanced features async thunks
export const fetchNotificationAnalytics = createAsyncThunk(
    'notifications/fetchNotificationAnalytics',
    async (params: {
        start?: string
        end?: string
        userId?: string
        type?: 'verification' | 'general'
    }, { rejectWithValue }) => {
        try {
            const response = await notificationApi.getNotificationAnalytics(params)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch notification analytics'
            return rejectWithValue(message)
        }
    }
)

export const trackNotificationEngagement = createAsyncThunk(
    'notifications/trackNotificationEngagement',
    async ({ notificationId, action }: { notificationId: string; action: 'opened' | 'clicked' | 'dismissed' }, { rejectWithValue }) => {
        try {
            await notificationApi.trackNotificationEngagement(notificationId, action)
            return { notificationId, action }
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to track notification engagement'
            return rejectWithValue(message)
        }
    }
)

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null
        },
        addNotification: (state, action: PayloadAction<Notification>) => {
            const notification = action.payload
            if (!state.notifications) {
                state.notifications = []
            }
            const existingIndex = state.notifications.findIndex(n => n._id === notification._id)

            if (existingIndex === -1) {
                state.notifications.unshift(notification)
                if (!notification.read) {
                    state.unreadCount += 1
                }
            }
        },
        updateNotification: (state, action: PayloadAction<Notification>) => {
            const updatedNotification = action.payload
            if (!state.notifications) {
                state.notifications = []
                return
            }
            const index = state.notifications.findIndex(n => n._id === updatedNotification._id)

            if (index !== -1) {
                const wasUnread = !state.notifications[index].read
                const isNowRead = updatedNotification.read

                state.notifications[index] = updatedNotification

                if (wasUnread && isNowRead) {
                    state.unreadCount = Math.max(0, state.unreadCount - 1)
                } else if (!wasUnread && !isNowRead) {
                    state.unreadCount += 1
                }
            }
        },
        removeNotification: (state, action: PayloadAction<string>) => {
            const notificationId = action.payload
            if (!state.notifications) {
                return
            }
            const index = state.notifications.findIndex(n => n._id === notificationId)

            if (index !== -1) {
                const notification = state.notifications[index]
                if (!notification.read) {
                    state.unreadCount = Math.max(0, state.unreadCount - 1)
                }
                state.notifications.splice(index, 1)
            }
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload
        },
        resetNotifications: (state) => {
            state.notifications = []
            state.unreadCount = 0
            state.hasMore = true
            state.currentPage = 1
            state.totalPages = 1
        },
        incrementUnreadCount: (state) => {
            state.unreadCount += 1
        },
        decrementUnreadCount: (state) => {
            state.unreadCount = Math.max(0, state.unreadCount - 1)
        },
        setUnreadCount: (state, action: PayloadAction<number>) => {
            state.unreadCount = action.payload
        },
        markNotificationAsRead: (state, action: PayloadAction<string>) => {
            const notificationId = action.payload
            if (!state.notifications) {
                return
            }
            const index = state.notifications.findIndex(n => n._id === notificationId)

            if (index !== -1 && !state.notifications[index].read) {
                state.notifications[index].read = true
                state.notifications[index].readAt = new Date().toISOString()
                state.unreadCount = Math.max(0, state.unreadCount - 1)
            }
        },
        markAllNotificationsAsRead: (state) => {
            if (!state.notifications) {
                state.notifications = []
                return
            }
            state.notifications = state.notifications.map(notification => ({
                ...notification,
                read: true,
                readAt: notification.readAt || new Date().toISOString()
            }))
            state.unreadCount = 0
        },
        // Document verification specific reducers
        addDocumentVerificationNotification: (state, action: PayloadAction<DocumentVerificationNotification>) => {
            const notification = action.payload
            if (!state.verificationNotifications) {
                state.verificationNotifications = []
            }
            const existingIndex = state.verificationNotifications.findIndex(n => n._id === notification._id)

            if (existingIndex === -1) {
                state.verificationNotifications.unshift(notification)
            }

            // Also add to main notifications array
            const mainIndex = state.notifications.findIndex(n => n._id === notification._id)
            if (mainIndex === -1) {
                state.notifications.unshift(notification)
                if (!notification.read) {
                    state.unreadCount += 1
                }
            }
        },
        setVerificationNotifications: (state, action: PayloadAction<Notification[]>) => {
            state.verificationNotifications = action.payload
        },
        setDocumentNotifications: (state, action: PayloadAction<{ documentId: string; notifications: Notification[] }>) => {
            const { documentId, notifications } = action.payload
            state.documentNotifications[documentId] = notifications
        },
        setVerificationStats: (state, action: PayloadAction<NotificationsState['verificationStats']>) => {
            state.verificationStats = action.payload
        },
        resetVerificationData: (state) => {
            state.verificationNotifications = []
            state.verificationStats = null
            state.documentNotifications = {}
        },
    },
    extraReducers: (builder) => {
        // Fetch Notifications
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.isLoading = false
                const { notifications, unreadCount, pagination } = action.payload

                // Ensure notifications array exists and state.notifications is initialized
                const incomingNotifications = notifications || []

                if (pagination.currentPage === 1) {
                    state.notifications = incomingNotifications
                } else {
                    state.notifications = [...(state.notifications || []), ...incomingNotifications]
                }

                state.unreadCount = unreadCount || 0
                state.currentPage = pagination.currentPage || 1
                state.totalPages = pagination.totalPages || 1
                state.hasMore = pagination.hasMore || false
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Mark as Read
        builder
            .addCase(markAsRead.fulfilled, (state, action) => {
                const updatedNotification = action.payload
                if (!state.notifications || !updatedNotification) {
                    return
                }
                const index = state.notifications.findIndex(n => n._id === updatedNotification._id)

                if (index !== -1) {
                    const wasUnread = !state.notifications[index].read
                    state.notifications[index] = updatedNotification

                    if (wasUnread) {
                        state.unreadCount = Math.max(0, state.unreadCount - 1)
                    }
                }
            })

        // Mark All as Read
        builder
            .addCase(markAllAsRead.fulfilled, (state, _action) => {
                if (!state.notifications) {
                    state.notifications = []
                    return
                }
                state.notifications = state.notifications.map(notification => ({
                    ...notification,
                    read: true,
                    readAt: notification.readAt || new Date().toISOString()
                }))
                state.unreadCount = 0
            })

        // Delete Notification
        builder
            .addCase(deleteNotification.fulfilled, (state, action) => {
                const notificationId = action.payload
                if (!state.notifications) {
                    return
                }
                const index = state.notifications.findIndex(n => n._id === notificationId)

                if (index !== -1) {
                    const notification = state.notifications[index]
                    if (!notification.read) {
                        state.unreadCount = Math.max(0, state.unreadCount - 1)
                    }
                    state.notifications.splice(index, 1)
                }
            })

        // Delete All Notifications
        builder
            .addCase(deleteAllNotifications.fulfilled, (state) => {
                state.notifications = []
                state.unreadCount = 0
                state.hasMore = false
                state.currentPage = 1
                state.totalPages = 1
            })

        // Fetch Unread Count
        builder
            .addCase(fetchUnreadCount.fulfilled, (state, action) => {
                state.unreadCount = action.payload
            })

        // Fetch Notification Preferences and Update Notification Preferences
        // are handled in Phase 4 below with complete pending/fulfilled/rejected cases

        // Document Verification Notifications
        builder
            .addCase(fetchVerificationNotifications.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchVerificationNotifications.fulfilled, (state, action) => {
                state.isLoading = false
                const { notifications } = action.payload
                state.verificationNotifications = notifications || []
            })
            .addCase(fetchVerificationNotifications.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Document Notifications
        builder
            .addCase(fetchDocumentNotifications.fulfilled, (state, action) => {
                const { documentId, data } = action.payload
                state.documentNotifications[documentId] = data.notifications || []
            })

        // Verification Stats
        builder
            .addCase(fetchVerificationStats.fulfilled, (state, action) => {
                state.verificationStats = action.payload
            })

        // Phase 4: Advanced features extraReducers
        builder
            .addCase(fetchNotificationPreferences.pending, (state) => {
                state.preferencesLoading = true
                state.error = null
            })
            .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
                state.preferencesLoading = false
                state.preferences = action.payload
            })
            .addCase(fetchNotificationPreferences.rejected, (state, action) => {
                state.preferencesLoading = false
                state.error = action.payload as string
            })

        builder
            .addCase(updateNotificationPreferences.pending, (state) => {
                state.preferencesLoading = true
                state.error = null
            })
            .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
                state.preferencesLoading = false
                state.preferences = action.payload
            })
            .addCase(updateNotificationPreferences.rejected, (state, action) => {
                state.preferencesLoading = false
                state.error = action.payload as string
            })

        builder
            .addCase(fetchNotificationAnalytics.pending, (state) => {
                state.analyticsLoading = true
                state.error = null
            })
            .addCase(fetchNotificationAnalytics.fulfilled, (state, action) => {
                state.analyticsLoading = false
                state.analytics = action.payload
            })
            .addCase(fetchNotificationAnalytics.rejected, (state, action) => {
                state.analyticsLoading = false
                state.error = action.payload as string
            })
    },
})

export const {
    clearError,
    addNotification,
    updateNotification,
    removeNotification,
    setCurrentPage,
    resetNotifications,
    incrementUnreadCount,
    decrementUnreadCount,
    setUnreadCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    // Document verification actions
    addDocumentVerificationNotification,
    setVerificationNotifications,
    setDocumentNotifications,
    setVerificationStats,
    resetVerificationData,
} = notificationsSlice.actions

export default notificationsSlice.reducer